import { computed, defineComponent, onMounted, PropType, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { useSweetAlert } from '@/composables/sweetalert';
import { MinisterPositionAction, MinisterPositionGetter } from '@/store/ministerPosition/ministerPosition.module';
import { MinisterPosition } from '@/store/ministerPosition/ministerPosition.model';
import { MinisterGetter } from '@/store/minister/minister.module';
import { Minister } from '@/store/minister/minister.model';
import { RegionAction, RegionGetter } from '@/store/region/region.module';
import { Region } from '@/store/region/region.model';
import { CabinetMinisterAction, CabinetMinisterGetter } from '@/store/cabinetMinister/cabinetMinister.module';
import { CabinetMinister } from '@/store/cabinetMinister/cabinetMinister.model';
import { CabinetMinisterSchema } from '@/store/cabinetMinister/cabinetMinister.validator';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';
import Multiselect from '@vueform/multiselect';
import _ from 'lodash';

export default defineComponent({
  name: 'CabinetMinisterForm',
  components: {
    BaseModalComponent,
    Multiselect,
  },
  props: {
    cabinetMinister: {
      type: Object as PropType<{
        cabinetId: number,
        ministerId?: number | null,
        ministerPositionId?: number | null,
        regions: string[]
      }>,
      required: true,
    },
  },
  emits: [
    'clear'
  ],
  setup: (props, context) => {
    const store = useStore();
    const swal = useSweetAlert();
    const isEditMode = computed(() => !!props.cabinetMinister?.ministerPositionId);
    const title = computed(() => isEditMode.value ? 'แก้ไขข้อมูล ร.นรม./รมต.นร. ในหนังสือคำสั่ง' : 'เพิ่มข้อมูล ร.นรม./รมต.นร. ในหนังสือคำสั่ง');
    const cabinetMinisters = computed<CabinetMinister[]>(() => store.getters[CabinetMinisterGetter.ALL]);
    const ministerPositions = computed<MinisterPosition[]>(() => store.getters[MinisterPositionGetter.ALL]);
    const selectedRegions = computed<string[]>(() => _.chain(cabinetMinisters.value).map(m => m.regions).flatten()
      .filter(m => props.cabinetMinister?.regions.every(r => r !== m.name)).map(r => r.name).value());
    const regions = computed<Region[]>(() => {
      const all = store.getters[RegionGetter.ALL] as Region[];
      return all.filter(m => m.id !== 1);
    });
    const regionOptions = computed<string[]>(() => _.chain(regions.value).filter(m => selectedRegions.value.every(x => m.name !== x)).map(r => r.name).value());
    const minister = computed<Minister | undefined>(() => store.getters[MinisterGetter.BY_ID](props.cabinetMinister?.ministerId));
    const cabinetMinisterForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      cabinetId: number;
      ministerId?: number | null;
      ministerPositionId?: number | null;
      regions: string[];
    }
    const formData = reactive<Form>({
      cabinetId: props.cabinetMinister?.cabinetId,
      ministerId: null,
      ministerPositionId: null,
      regions: []
    });
    const validator = computed(() => CabinetMinisterSchema.validate(formData, { abortEarly: false }));
    const errors = reactive({
      ministerPositionId: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'ministerPositionId')),
      regions: computed(() => validator.value.error?.details.some((detail) => detail.path[0] === 'regions')),
    });
    const isValid = computed(() => !errors.ministerPositionId  && !errors.regions);
    const isLoading = computed(() => store.getters[CabinetMinisterGetter.STATUS] === 'init' || store.getters[CabinetMinisterGetter.STATUS] === 'loading');
    const submited = ref(false);

    onMounted(() => {
      store.dispatch(MinisterPositionAction.FETCH);
      store.dispatch(RegionAction.FETCH);
    });

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.ministerId = props.cabinetMinister?.ministerId;
        if (isEditMode.value) {
          formData.ministerPositionId = props.cabinetMinister?.ministerPositionId;
          formData.regions = props.cabinetMinister?.regions;
        }
        resolve();
      })
      .then(() => cabinetMinisterForm.value?.show());
    };

    // hide modal
    const hide = () => {
      cabinetMinisterForm.value?.hide();
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      context.emit('clear');
      new Promise<void>((resolve) => {
        formData.ministerId = null,
        formData.ministerPositionId = null,
        formData.regions = [],
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(CabinetMinisterAction.UPDATE, formData)
            .then(() => hide());
        } else {
          store.dispatch(CabinetMinisterAction.CREATE, formData)
            .then(() => hide());
        }

        clearForm();
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(CabinetMinisterAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      isEditMode,
      errors,
      submited,
      isLoading,
      ministerPositions,
      regionOptions,
      minister,
      cabinetMinisterForm,
      show,
      hide,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
